import { useMutation } from "@tanstack/react-query";
import { ShieldAlert } from "lucide-react";
import { useState } from "react";

import type {
  OpsSupportLaunchResponse,
  OpsSupportSession,
  OpsSupportTargetTool,
} from "../../shared/operations-schemas";
import { Button } from "../components/ui/button";
import { FieldError } from "../components/ui/field";
import { launchSupportTool } from "./api";

/**
 * One live support session, and the controls that open a tool with it.
 *
 * Shared by both paths on the Support access page. What differs between them is not this card but
 * the `tools` it is handed: an approval scoped to one tool passes that one tool, so the picker
 * cannot offer something the server would refuse. Filtering here instead would put the approved
 * scope in two places, and the copy in the browser is the one that would drift.
 */
/**
 * One tool, named; or several, offered. Split out because the two cases read very differently and
 * a select holding a single option is a control that pretends there is a decision to make.
 */
function ToolChoice({
  tools,
  onlyTool,
  selectedToolId,
  onChange,
}: {
  tools: OpsSupportTargetTool[];
  onlyTool: OpsSupportTargetTool | undefined;
  selectedToolId: string;
  onChange: (toolId: string) => void;
}) {
  if (onlyTool) return <span className="text-sm font-medium">{onlyTool.displayName}</span>;
  return (
    <select
      className="h-9 min-w-52 rounded-md border bg-background px-3 text-sm"
      aria-label="Customer tool"
      value={selectedToolId}
      onChange={(event) => {
        onChange(event.currentTarget.value);
      }}
    >
      {tools.map((tool) => (
        <option key={tool.id} value={tool.id}>
          {tool.displayName}
        </option>
      ))}
    </select>
  );
}

export function SupportSessionCard({
  organizationId,
  session,
  tools,
  onLaunch,
  onEnd,
  ending,
}: {
  organizationId: string;
  session: OpsSupportSession;
  /** Already narrowed to what this session may reach. */
  tools: OpsSupportTargetTool[];
  onLaunch: (launch: OpsSupportLaunchResponse) => void;
  onEnd: (supportSessionId: string) => void;
  ending: boolean;
}) {
  const [toolId, setToolId] = useState("");
  const selectedToolId = toolId === "" ? (tools[0]?.id ?? "") : toolId;
  const launch = useMutation({
    mutationFn: () =>
      launchSupportTool(organizationId, session.id, {
        appInstanceId: selectedToolId,
        intendedPath: "/",
      }),
    onSuccess: onLaunch,
  });
  const onlyTool = tools.length === 1 ? tools[0] : undefined;
  return (
    <div className="rounded-sm border border-amber-500/50 bg-amber-500/5 p-3">
      <p className="flex items-center gap-2 text-sm font-semibold">
        <ShieldAlert className="size-4" /> {session.mode === "write" ? "Write" : "Read-only"}{" "}
        support session active
      </p>
      <p className="mt-1 text-xs text-muted-foreground">
        {session.reason} · expires {new Date(session.expiresAt * 1_000).toLocaleTimeString()}
      </p>
      {session.targetUserId && tools.length > 0 ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <ToolChoice
            tools={tools}
            onlyTool={onlyTool}
            selectedToolId={selectedToolId}
            onChange={setToolId}
          />
          <Button
            type="button"
            size="sm"
            disabled={!selectedToolId || launch.isPending}
            onClick={() => {
              launch.mutate();
            }}
          >
            Open as target user
          </Button>
          {launch.error ? <FieldError>{launch.error.message}</FieldError> : null}
        </div>
      ) : null}
      <Button
        className="mt-3"
        type="button"
        variant="outline"
        size="sm"
        disabled={ending}
        onClick={() => {
          onEnd(session.id);
        }}
      >
        End session
      </Button>
    </div>
  );
}
